// React-Query-Hooks fuer die Article-Domaene (Session 23).

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  articlesApi,
  type ArticleExplorerQuery,
  type ArticleCreateInput,
  type ArticleFromLibraryInput,
  type ArticleListQuery,
  type ArticleUpdateInput,
} from "../api";
import type { Article, ArticleWithVariants } from "../types";
import { useRealtimeRecoveryInterval } from "../../../realtime/realtimeEvents";
import type { ArticleDesignDetail } from "../types";

const keys = {
  all: ["articles"] as const,
  list: (q: ArticleListQuery) => ["articles", "list", q] as const,
  detail: (id: string) => ["articles", "detail", id] as const,
  design: (designKey: string) => ["articles", "design", designKey] as const,
  gtinPool: ["gtin-pool", "status"] as const,
};

export function useArticles(query: ArticleListQuery = {}) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => articlesApi.list(query),
  });
}

export function useArticle(id: string | null | undefined) {
  return useQuery<ArticleWithVariants>({
    queryKey: keys.detail(id ?? ""),
    queryFn: () => articlesApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useArticleDesign(designKey: string | null | undefined) {
  return useQuery<ArticleDesignDetail>({
    queryKey: keys.design(designKey ?? ""),
    queryFn: () => articlesApi.getDesign(designKey as string),
    enabled: Boolean(designKey),
  });
}

export function useUpdateArticleDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      designKey,
      data,
    }: {
      designKey: string;
      data: { name?: string; shopLabels?: Record<string, string> };
    }) => articlesApi.updateDesign(designKey, data),
    onSuccess: (_design, { designKey }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.design(designKey) });
    },
  });
}

/// Fortschritt der Bild-Generierung: Queue-SSE aktualisiert sofort;
/// 60 s REST bleiben als Recovery bei Stream-/Redis-Ausfall.
export function useImagesStatus() {
  const recoveryInterval = useRealtimeRecoveryInterval();
  return useQuery({
    queryKey: ["articles", "images-status"],
    queryFn: () => articlesApi.imagesStatus(),
    refetchInterval: recoveryInterval,
    staleTime: 10000,
  });
}

/// GTIN-Pool-Status (free/reserved/used/total). Speist den "X GTINs frei"-Badge
/// im Artikel-Header und im Export-Tab. Wird nach echtem Export invalidiert.
export function useGtinStatus(options?: { enabled?: boolean }) {
  const recoveryInterval = useRealtimeRecoveryInterval();
  return useQuery({
    queryKey: keys.gtinPool,
    queryFn: () => articlesApi.gtinPoolStatus(),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    // GS1-Queue-Ereignisse aktualisieren sofort; REST ist nur Recovery.
    refetchInterval: (q) => {
      const data = q.state.data as { pending?: number; assignmentMissing?: number } | undefined;
      return (data?.pending ?? 0) > 0 || (data?.assignmentMissing ?? 0) > 0
        ? recoveryInterval
        : false;
    },
  });
}

/// Produkt-/Nischen-Filteroptionen und Gesamtzahl — unabhängig von der Pagination.
export function useArticleFacets() {
  return useQuery({
    queryKey: ["articles", "facets"],
    queryFn: () => articlesApi.facets(),
  });
}

export function useArticleExplorer(filter: ArticleExplorerQuery = {}) {
  return useInfiniteQuery({
    queryKey: ["articles", "explorer", filter],
    queryFn: ({ pageParam, signal }) =>
      articlesApi.explorer({ ...filter, cursor: pageParam as number, limit: 50 }, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  });
}

export function useListingWizardActivity() {
  const recoveryInterval = useRealtimeRecoveryInterval();
  return useQuery({
    queryKey: ["articles", "listing-wizard-activity"],
    queryFn: () => articlesApi.listingWizardActivity(),
    staleTime: 15_000,
    refetchInterval: (query) =>
      (query.state.data?.activities.length ?? 0) > 0 ? recoveryInterval : false,
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ArticleCreateInput) => articlesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useCreateArticlesFromLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ArticleFromLibraryInput) => articlesApi.fromLibrary(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ArticleUpdateInput }) =>
      articlesApi.update(id, data),
    onSuccess: (_article, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useUpdatePrinteraMotifSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      designKey,
      shopLabels,
    }: {
      designKey: string;
      shopLabels: Record<string, string>;
    }) => articlesApi.updatePrinteraMotifSettings(designKey, { shopLabels }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

/** Artikelstamm v2: gespeicherten Parent-Stand auf alle Childs anwenden. */
export function useApplyContentToChildren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => articlesApi.rematerializeChildren(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => articlesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useBatchDeleteArticles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (articleIds: string[]) => articlesApi.batchDelete(articleIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useGenerateVariants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      articlesApi.generateVariants(id, { force }),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useGenerateMetadata() {
  return useMutation({
    mutationFn: ({ articleId, locale }: { articleId: string; locale: "de" | "en" }) =>
      articlesApi.generateMetadata(articleId, locale),
  });
}

/// Per-Variant-Override (Preis/Bestand/GTIN, S49: + Titel/Bullets)
/// editieren — invalidiert das Detail.
export function useUpdateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      articleId,
      variantId,
      data,
    }: {
      articleId: string;
      variantId: string;
      data: {
        price?: number | null;
        stock?: number | null;
        gtin?: string | null;
        title?: string | null;
        bullets?: string[];
        countryOfOriginOverride?: string | null;
        contentOverrides?: import("../types").ArticleVariantContentOverrides;
        attributes?: Record<string, string>;
        attributeGroups?: Record<string, string[]>;
      };
    }) => articlesApi.updateVariant(articleId, variantId, data),
    onSuccess: (_v, { articleId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(articleId) });
      // Phase C: Preis/Bestand-Save markiert das Listing dirty — sofort
      // refetchen, damit „Sync ausstehend" ohne 20-s-Poll sichtbar wird.
      qc.invalidateQueries({ queryKey: ["marketplace-push-status"] });
    },
  });
}
